import { BusinessActivity } from '@/types/businessAppTypes';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit2, Trash2, Users, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { motion } from 'framer-motion';

interface ActivityGridProps {
  activities: BusinessActivity[];
  onEdit: (activity: BusinessActivity) => void;
  onDelete: (id: string) => void;
}

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: "easeOut"
    }
  })
};

export function ActivityGrid({ activities, onEdit, onDelete }: ActivityGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {activities.map((activity, index) => (
        <motion.div
          key={activity._id}
          custom={index}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <Card className="overflow-hidden group hover:shadow-md transition-shadow">
            <CardHeader className="relative p-0">
              <div className="aspect-video w-full bg-gray-100 relative">
                {activity.images && activity.images[0] ? (
                  <img
                    src={activity.images[0]}
                    alt={activity.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No image available
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge
                    variant={activity.isActive ? "default" : "secondary"}
                    className={activity.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}
                  >
                    {activity.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-lg">{activity.name}</h3>
                  <Badge variant="outline" className="mt-1 capitalize">
                    {activity.activityType}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 line-clamp-2">
                  {activity.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{activity.maxParticipants} max</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    <span>
                      {formatCurrency(
                        activity.pricing?.defaultPrices?.adult?.amount || 0,
                        activity.pricing?.defaultPrices?.adult?.currency || 'USD'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-4 pt-0 flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(activity)}
                className="hover:bg-gray-100"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => onDelete(activity._id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      ))}
    </div>
  );
} 